import {
    createContext,
    useContext,
    useState,
    useEffect,
    useCallback,
} from "react";
import { Button, Col, Row, Container } from "react-bootstrap";
import Loader2 from "../../../components/Loader2.jsx";
import TransactionsList from "./components/TransactionsList.jsx";
import UploadExcelModal from "./components/TransactionModal.jsx";
import api from "../../../service/api.js";
import NotyfContext from "../../../contexts/NotyfContext.jsx";
import { fetchUnpaidInvoices } from "./hooks/api.js";
import AssignSalespersonModal from "./components/AssignSalespersonModal.jsx";
import TransactionSearch from "./components/TransactionSearch.jsx";
import { useLocation } from "react-router-dom";
import download from "downloadjs";
import { SnackbarAlert } from "../../../components/SnackbarAlert.js";
import OverPaidModal from "./components/OverpaidModal.jsx";

export const ContextProvider = createContext(null);

export default function BankTransactions() {
    const notyf = useContext(NotyfContext);

    const context = null;
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [data, setData] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [perPage, setPerPage] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [lastPage, setLastPage] = useState(1);
    const [salespersons, setSalespersons] = useState([]);


    const [showSalesModal, setShowSalesModal] = useState(false);
    const [showOverpaidModal, setShowOverpaidModal] = useState(false);
    const [selectedRow, setSelectedRow] = useState(null);

    const location = useLocation();
    const accountType = location.pathname.includes("fgi-ac1") ? "AC1" : "AC2";

    const [showUploadModal, setShowUploadModal] = useState(false);
    const [search, setSearchValue] = useState({
        transaction_id: "",
        status: "OPEN-PARTIAL",
        company: "",
        start_date: null,
        end_date: null,
        version: accountType === "AC1" ? "AC1" : "AC2",
    });

    const [notif, setNotif] = useState({
        notifMsg: "",
        open: false,
        severity: "success",
    });

    const handleUpload = async (formData) => {
        formData.append("type", accountType);
        await api.post("tms/bank-transaction/upload", formData);
        notyf?.success("File uploaded successfully!");
        getTransaction(search, 1);
    };

    const handleOpenSalesModal = (row) => {
        setSelectedRow(row);
        setShowSalesModal(true);
    };

    const handleOpenOverpaid = (row) => {
        setSelectedRow(row);
        setShowOverpaidModal(true);
    };


    const getTransaction = useCallback(
        async (searchParams, page = 1) => {
            try {
                setError("");
                setLoading(true);

                const response = await fetchUnpaidInvoices({
                    ...searchParams,
                    page,
                });

                setData(response.data);                 // current page
                setTotalCount(response.total);          // total records
                setPerPage(response.per_page);          // per page
                setCurrentPage(response.current_page);  // current page
                setLastPage(response.last_page);        // total pages
            } catch (error) {
                setError(error.message || "An error occurred during fetching of data.");
            } finally {
                setLoading(false);
            }
        },
        []
    );

    const exportToExcel = async () => {
        try {
            // Await the API call
            const response = await api.getexcel("tms/bank-transaction/export", search);

            // Check content type
            const contentType = response.type || response.headers?.get?.("content-type");

            // If the server returned JSON instead of Excel, treat it as error
            if (contentType?.includes("application/json")) {
                const text = await response.text();
                let jsonData = {};
                try {
                    jsonData = JSON.parse(text);
                } catch {
                    setError("Unexpected response from server.");
                    return;
                }

                if (jsonData.status === "ok" && jsonData.message) {
                    setError(jsonData.message || "No data to export.");
                    return;
                }

                if (jsonData.error) {
                    setError(jsonData.error || "An error occurred during export.");
                    return;
                }

                // Fallback in case unknown JSON structure
                setError("Failed to export. Server returned an error.");
                return;
            }

            // Only download if it’s a proper file
            const fileName = `Bank_Transactions_${accountType}_${new Date()
                .toISOString()
                .slice(0, 10)}.xlsx`;

            download(response, fileName, "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet");

            setNotif({
                notifMsg: "Exported successfully!",
                open: true,
                severity: "success",
            });
        } catch (error) {
            console.error(error);
            setNotif({
                notifMsg: "Export failed!",
                open: true,
                severity: "danger",
            });
        }
    };

    const fetchSalespersons = async () => {
        try {
            const res = await api.get("tms/get-sales-persons");
            setSalespersons(res ?? []);
        } catch (error) {
            console.error(error);
        }
    };
    useEffect(() => {
        const freshSearch = {
            transaction_id: "",
            status: "OPEN-PARTIAL",
            company: "",
            start_date: null,
            end_date: null,
            version: accountType,
        };
        setSearchValue(freshSearch);
        getTransaction(freshSearch, 1);
        fetchSalespersons();
    }, [accountType, getTransaction]);

    return (
        <ContextProvider.Provider value={context}>
            <Container fluid>
                <Row>
                    <Col md={12}>
                        <h3 className="fw-bolder m-0">Bank Transactions FGI - {accountType}</h3>
                    </Col>
                </Row>

                <Row>
                    <Col md={12}>
                        <TransactionSearch
                            setSearchValue={setSearchValue}
                            searchValue={search}
                            onSearch={() => getTransaction(search, 1)}
                            salesPersons={salespersons}
                        />
                    </Col>
                </Row>

                <Row className="mt-3 mb-4">
                    <Col md={12} className="d-flex justify-content-end gap-2">
                        <Button onClick={() => exportToExcel()}>Export Excel</Button>

                        <Button onClick={() => setShowUploadModal(true)}>Upload Excel</Button>
                    </Col>
                </Row>

                <Row>
                    <Col xxl={12}>
                        {loading ? (
                            <div className="text-center py-5">
                                <Loader2 />
                            </div>
                        ) : (
                            <TransactionsList
                                transaction={data}
                                onAssignSalesperson={handleOpenSalesModal}
                                currentPage={currentPage}
                                perPage={perPage}
                                totalRecords={totalCount}
                                lastPage={lastPage}
                                onPageChange={(page) => getTransaction(search, page)}
                                onOverpaid={handleOpenOverpaid}
                            />
                        )}
                    </Col>
                </Row>

                <UploadExcelModal
                    show={showUploadModal}
                    onClose={() => setShowUploadModal(false)}
                    onSubmit={handleUpload}
                />

                <AssignSalespersonModal
                    show={showSalesModal}
                    onHide={() => setShowSalesModal(false)}
                    row={selectedRow}
                    onUpdated={() => getTransaction(search, currentPage)}
                    salespersons={salespersons}
                />

                <OverPaidModal
                    show={showOverpaidModal}
                    onHide={() => setShowOverpaidModal(false)}
                    row={selectedRow}
                    onUpdated={() => getTransaction(search, currentPage)}
                />
            </Container>
            <SnackbarAlert notif={notif} setNotif={setNotif} />
            
        </ContextProvider.Provider>
    );
}